import {
  TextInput,
  required,
  SelectInput,
  ReferenceInput,
  SimpleFormIterator,
  ArrayInput,
  SimpleForm,
  useCreate,
  useSimpleFormIteratorItem,
  FormDataConsumer,
  useTranslate,
  useRedirect,
  useNotify,
  AutocompleteInput,
} from "react-admin";
import { Grid, Typography } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import dataProvider from "server/dataProvider";
import { CampaignSubTypes, ClassTypes, PRELIMINARY } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import Declarations from "../components/Declarations";
import Ibans from "../components/Ibans";
import ExamList from "../components/ExamList";
import { useLocation } from "react-router-dom";
import campaignHelper from "../helpers";
import CitizenshipInput from "../components/Inputs/CitizenshipInput";
import CampaignTypeInput from "../components/Inputs/CampaignTypeInput";
import DirectionInput from "../components/Inputs/DirectionInput";
import Taxes from "../components/Taxes";
import { settings } from "app";
import CustomToolbar from "../components/CustomToolbar";
import AdmissionMarkerInput from "../components/Inputs/AdmissionMarkerInput";
import { useNotifyError } from "utils/useNotifyError";

const validateExamDate = (value, values) => {
  if (!value) {
    return "Полето е задължително";
  }

  const valueRegistrationTo = values.registrationTo;
  if (valueRegistrationTo && valueRegistrationTo >= value) {
    return "Датата на изпита трябва да е след датата за край на кампания";
  }

  const dateMap = new Map();
  Object.keys(values).forEach(key => {
    if (key.startsWith("examTimestamp")) {
      const timestamp = values[key];
      const dateOnly = timestamp?.split("T")[0];
      if (dateOnly) {
        if (dateMap.has(dateOnly)) {
          dateMap.set(dateOnly, false);
          return;
        } else {
          dateMap.set(dateOnly, true);
        }
      }
    }
  });

  const valueStartTime = value?.split("T")[0];
  if (dateMap.has(valueStartTime)) {
    if (!dateMap.get(valueStartTime)) {
      return "Датите на изпитите трябва да са в различни дни";
    }
  }
};

const formatDate = date => {
  const correctTimeZoneOffset = 2 * 60;
  date = new Date(date);
  date.setMinutes(date.getMinutes() + correctTimeZoneOffset);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const milliseconds = String(date.getUTCMilliseconds()).padStart(7, "0");
  const timezoneOffset = "+02:00";

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezoneOffset}`;
};

const SaveForm = ({ isClone = false }) => {
  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const location = useLocation();

  const defaultValues = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get("source");
    if (source) {
      try {
        return JSON.parse(decodeURIComponent(source));
      } catch (e) {
        console.error("Invalid source:", e);
      }
    }
    return {};
  }, [location.search]);
  const validateStartDate = async (value, allValues) => {
    if (!value) {
      return "Задължително";
    }
    if (value >= allValues.registrationTo) {
      return "Началната дата трябва да е по-малка от крайната";
    }

    return await campaignHelper.checkAvailability({
      typeId,
      directionId,
      startDate: value,
      endDate: allValues.registrationTo,
      citizenshipIds,
      notify,
    });
  };

  const validateEndDate = async (value, allValues) => {
    if (!value) {
      return "Задължително";
    }
    if (value <= allValues.registrationFrom) {
      return "Крайната дата трябва да е по-голяма от началната";
    }

    return await campaignHelper.checkAvailability({
      typeId,
      directionId,
      startDate: allValues.registrationFrom,
      endDate: value,
      citizenshipIds,
      notify,
    });
  };
  const campaignSave = data => {
    setIsSaving(true);
    data.registrationFrom = formatDate(data.registrationFrom);
    data.registrationTo = formatDate(data.registrationTo);

    data.examDtos = exams.map((item, index) => ({
      examId: item.examId,
      examTimestamp: formatDate(data[`examTimestamp${index}`]),
      rooms: data.rooms.map((room, roomIndex) => ({
        roomId: room.roomId,
        priority: ++roomIndex,
      })),
    }));
    create(
      candidateRequests.campaignByType(PRELIMINARY),
      { data },
      {
        onSuccess: () => {
          redirect("list", candidateRequests.campaignByType(PRELIMINARY));
        },
        onError: error => {
          notify(`Error: ${error.message || "Грешка при създаване на предварителна кампания"}`, { type: "error" });
          setIsSaving(false);
        },
      },
    );
  };

  const handleStartDateChange = value => {
    setMinEndDate(value?._i || null);
  };

  const [minEndDate, setMinEndDate] = useState(null);
  const [excludedSpecialityIds, setExcludedSpecialityIds] = useState(isClone ? defaultValues?.specialitiesIds : []);
  const [excludedRoomsIds, setExcludedRoomsIds] = useState([]);
  const [directionId, setDirectionId] = useState(defaultValues?.directionId);
  const [typeId, setTypeId] = useState(null);
  const [exams, setExams] = useState(defaultValues?.exams || []);
  const [roomsCapacity, setRoomsCapacity] = useState(null);
  const [citizenshipIds, setCitizenshipIds] = useState(defaultValues?.citizenship || []);
  const [isSaving, setIsSaving] = useState(false);

  const fetchExamSpecialities = useCallback(
    async selectedSpecialityIds => {
      setExcludedSpecialityIds(selectedSpecialityIds);
      if (!selectedSpecialityIds?.length) {
        setExams([]);
        return;
      }
      try {
        const { data } = await dataProvider.customRequest(
          administrationRequests.getExamSpecialitiesSelect(selectedSpecialityIds),
          null,
          "GET",
        );

        const newExams = data.map(item => ({
          examId: item.id,
          name: item.name,
        }));
        setExams(newExams);
      } catch (error) {
        notifyError(error);
      }
    },
    [notifyError],
  );

  useEffect(() => {
    if (isClone) {
      fetchExamSpecialities(excludedSpecialityIds);
    }
  }, [excludedSpecialityIds, isClone, fetchExamSpecialities]);

  const citizenshipChange = event => {
    setCitizenshipIds(event.target.value);
  };

  const handleSpecialityChange = async (event, formDataSpecialities) => {
    const selectedSpecialitesIds = formDataSpecialities
      ? formDataSpecialities.map(item => item.specialityId).filter(Boolean)
      : [];
    fetchExamSpecialities(selectedSpecialitesIds);
  };

  const handleRoomChange = async (event, formDataRooms, classTypes, showExamCapacity) => {
    const selectedRoomsIds = formDataRooms ? formDataRooms.map(item => item.roomId).filter(Boolean) : [];
    setExcludedRoomsIds(selectedRoomsIds);

    if (selectedRoomsIds.length === 0) {
      setRoomsCapacity(null);
      return;
    }

    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getRoomsCapacitiesSelect(selectedRoomsIds, classTypes, showExamCapacity),
        null,
        "GET",
      );

      setRoomsCapacity(data);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleTypeChange = event => {
    setTypeId(event?.target?.value || event);
  };

  const handleDirectionChange = newDirectionId => {
    setDirectionId(newDirectionId);
    setExcludedSpecialityIds([]);

    if (!typeId || !directionId) {
      return;
    }
  };

  const handleRemoveSpeciality = removedId => {
    const updatedSpecialityIdsIds = excludedSpecialityIds.filter(id => id !== removedId);
    fetchExamSpecialities(updatedSpecialityIdsIds);
  };

  const handleRemoveRoom = async (removedId, classTypes, showExamCapacity) => {
    setExcludedRoomsIds(prevIds => prevIds.filter(id => id !== removedId));

    const updatedExcludedRoomsIds = excludedRoomsIds.filter(id => id !== removedId);

    if (updatedExcludedRoomsIds.length === 0) {
      setRoomsCapacity(null);
      return;
    }

    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getRoomsCapacitiesSelect(updatedExcludedRoomsIds, classTypes, showExamCapacity),
        null,
        "GET",
      );

      setRoomsCapacity(data);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleRemoveExamByIndex = indexToRemove => {
    setExams(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const CustomRemoveButton = ({ record, classTypes, showExamCapacity }) => {
    const translate = useTranslate();
    const { remove } = useSimpleFormIteratorItem();

    const handleClick = () => {
      if (record.specialityId) {
        handleRemoveSpeciality(record.specialityId);
      }
      if (record.roomId) {
        handleRemoveRoom(record.roomId, classTypes, showExamCapacity);
      }
      remove();
    };
    const title = translate("ra.action.remove");
    return (
      <Tooltip title={title}>
        <IconButton
          color="warning"
          onClick={handleClick}
        >
          <RemoveCircleOutlineIcon />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <SimpleForm
      onSubmit={campaignSave}
      defaultValues={defaultValues}
      toolbar={<CustomToolbar isSaving={isSaving} />}
    >
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          <AdmissionMarkerInput />
        </Grid>

        <Grid
          item
          xs={8}
        >
          <TextInput
            label="Име на български език"
            source="nameBg"
            validate={required()}
          />
          <TextInput
            label="Име на английски език"
            source="nameEn"
            validate={required()}
          />
        </Grid>
        <Grid
          item
          xs={4}
        >
          <CitizenshipInput
            isArray
            isBgAndForeignersSeperated
            handleChange={citizenshipChange}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            label="Начало на регистрация"
            source="registrationFrom"
            required={true}
            validate={validateStartDate}
            onChange={event => {
              handleStartDateChange(event);
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2.3}
        >
          <CustomDateTimeInput
            label="Край на регистрация"
            source="registrationTo"
            required={true}
            validate={validateEndDate}
            inputProps={{
              min: minEndDate,
            }}
            helperText={false}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <CampaignTypeInput
            subTypeId={CampaignSubTypes.Preliminary}
            handleChange={handleTypeChange}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <DirectionInput
            required
            handleChange={handleDirectionChange}
            filterByUser
            filterOnlyForCampaigns
          />
        </Grid>
        {directionId && (
          <>
            <Grid
              item
              xs={12}
              md={12}
            >
              <Typography color={"primary"}>Специалности</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
            >
              <ArrayInput
                source="specialityDtos"
                label=" "
                defaultValue={[{}]}
              >
                <SimpleFormIterator
                  disableReordering
                  disableRemove
                  inline
                  sx={settings.style.iteratorWithoutGap}
                >
                  <FormDataConsumer>
                    {({ scopedFormData, formData }) => {
                      return (
                        <Grid
                          container
                          spacing={2}
                        >
                          <Grid
                            item
                            xs={12}
                            md={11}
                          >
                            <ReferenceInput
                              source="specialityId"
                              reference={administrationRequests.getSpecialitiesSelect()}
                              filter={{
                                excludeIds: (excludedSpecialityIds || []).filter(Boolean),
                                directionId: directionId,
                                filterByUser: true,
                              }}
                            >
                              <SelectInput
                                label="Специалност"
                                onChange={event => handleSpecialityChange(event, formData.specialityDtos)}
                              />
                            </ReferenceInput>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={1}
                          >
                            <CustomRemoveButton
                              record={scopedFormData} // Pass the current record
                            />
                          </Grid>
                        </Grid>
                      );
                    }}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>

            <ExamList
              exams={exams}
              onRemoveExam={handleRemoveExamByIndex}
              validateExamDate={validateExamDate}
              isClone={isClone}
            />

            <Grid
              item
              container
            >
              <Grid
                item
                xs={12}
                md={12}
              >
                <Typography color={"primary"}>Зали</Typography>
                <ArrayInput
                  fullWidth
                  source="rooms"
                  label=""
                  defaultValue={[{}]}
                >
                  <SimpleFormIterator
                    disableRemove
                    sx={settings.style.iteratorWithoutGap}
                  >
                    <FormDataConsumer>
                      {({ scopedFormData, formData }) => {
                        return (
                          <Grid
                            container
                            spacing={1}
                          >
                            <Grid
                              item
                              xs={12}
                              md={10}
                            >
                              <ReferenceInput
                                source="roomId"
                                reference={administrationRequests.getRoomsSelect()}
                                filter={{
                                  ClassTypes: ClassTypes.Csc,
                                  excludeIds: excludedRoomsIds.filter(Boolean),
                                  filterByUser: true,
                                }}
                              >
                                <AutocompleteInput
                                  label="Зала"
                                  optionValue="id"
                                  optionText={rec =>
                                    `${rec.roomName} - ${rec.buildingName}${
                                      rec.capacityExam ? ` (${rec.capacityExam})` : ""
                                    }`
                                  }
                                  filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                                  onChange={event => handleRoomChange(event, formData.rooms, 1, true)}
                                  validate={required()}
                                  fullWidth
                                />
                              </ReferenceInput>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={2}
                            >
                              <CustomRemoveButton
                                record={scopedFormData}
                                classTypes={ClassTypes.Csc}
                                showExamCapacity={true}
                              />
                            </Grid>
                          </Grid>
                        );
                      }}
                    </FormDataConsumer>
                  </SimpleFormIterator>
                </ArrayInput>
              </Grid>
            </Grid>
            {roomsCapacity && (
              <Grid
                item
                xs={12}
                md={5}
              >
                <Typography color={"secondary"}>Общ капацитет на залите: {roomsCapacity}</Typography>
              </Grid>
            )}
          </>
        )}
        <Declarations
          citizenshipIds={citizenshipIds}
          isDisabled={false}
        />
        <Taxes
          campaignSubTypeId={CampaignSubTypes.Preliminary}
          citizenshipIds={citizenshipIds}
          examsCount={exams?.length || 0}
          specialityIds={excludedSpecialityIds || []}
          isCreate
        />
        <Ibans />
      </Grid>
    </SimpleForm>
  );
};
export default SaveForm;
